import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Warning, Trash, CircleNotch } from '@phosphor-icons/react';

export default function DeleteConfirmDialog({ open, onOpenChange, entityName, itemName, fetchDependencies, onConfirmDelete }) {
  const [dependencies, setDependencies] = useState([]);
  const [hasDeps, setHasDeps] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && fetchDependencies) {
      setLoading(true);
      fetchDependencies()
        .then(res => {
          setDependencies(res.data.dependencies || []);
          setHasDeps(res.data.has_dependencies || false);
        })
        .catch(() => setDependencies([]))
        .finally(() => setLoading(false));
    }
  }, [open, fetchDependencies]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirmDelete();
      onOpenChange(false);
    } catch {
      // error handled by parent
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md" aria-describedby="delete-desc">
        <DialogHeader>
          <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
            <Trash size={22} className="text-red-500" />
            Eliminar {entityName}
          </DialogTitle>
          <DialogDescription id="delete-desc" className="text-[#2B4B8C]/60">
            Esta acción no se puede deshacer
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <CircleNotch size={24} className="animate-spin text-[#2B4B8C]/40" />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-[#2B4B8C]">
              ¿Estás seguro de eliminar <strong>{itemName}</strong>?
            </p>

            {hasDeps && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-2">
                  <Warning size={20} className="text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-700">Tiene registros asociados:</p>
                    <ul className="mt-1 space-y-0.5">
                      {dependencies.map((dep, i) => (
                        <li key={i} className="text-sm text-amber-600">- {dep}</li>
                      ))}
                    </ul>
                    <p className="text-xs text-amber-500 mt-2">Todos serán eliminados permanentemente.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-[#2B4B8C]/10" data-testid="cancel-delete-btn">
                Cancelar
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 text-white hover:bg-red-600"
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
